import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ModalContentProps } from '../../types';
import { useToast } from '../../context/toasts/useToast';

const listSchema = z.object({
  name: z
    .string()
    .min(1, 'List name is required')
    .max(100, 'List name must be less than 100 characters'),
});

type ListFormData = z.infer<typeof listSchema>;

interface CreateListModalProps extends ModalContentProps {
  onCreate: (name: string) => Promise<void>;
}

export function CreateListModal({ onClose, onCreate }: CreateListModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ListFormData>({
    resolver: zodResolver(listSchema),
  });

  const { addNotification } = useToast();

  const onSubmit = async (data: ListFormData) => {
    try {
      await onCreate(data.name);
      onClose();
    } catch (err) {
      // Error is handled by caller

      const errorMessage = err instanceof Error ? err.message : 'Failed to create card';
      addNotification({
        type: 'error',
        title: 'Error Register',
        message: errorMessage,
        duration: 5000,
      });
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md border border-gray-700">
      <div className="flex justify-between items-center p-6 border-b border-gray-700">
        <h2 id="modal-title" className="text-xl font-bold text-white">
          Create List
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6">
        <div className="mb-6">
          <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
            List Name <span className="text-red-400">*</span>
          </label>
          <input
            id="name"
            type="text"
            {...register('name')}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., To Do, In Progress, Done"
            autoFocus
          />
          {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>}
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 text-gray-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Create List
          </button>
        </div>
      </form>
    </div>
  );
}
